import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ScanFace, Car, Focus, Scan, Smile, Mic, UploadCloud,
  SlidersHorizontal, Download, Quote,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { TOOLS, STEPS, USE_CASES, STATS, TESTIMONIALS, FAQS, IMAGES } from "../mock/mock";

const TOOL_ICONS = { ScanFace, Car, Focus, Scan, Smile, Mic };
const STEP_ICONS = { UploadCloud, SlidersHorizontal, Download };

/* ---------- estadísticas ---------- */
export const StatsSection = () => (
  <section className="py-10 border-y border-[#F0E7DD] bg-white">
    <div className="max-w-[1240px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
      {STATS.map((s) => (
        <div key={s.label} className="text-center">
          <p className="font-display text-[32px] sm:text-[38px] font-extrabold text-[#F26F21]">{s.value}</p>
          <p className="text-[13px] text-[#8A7F73] font-semibold">{s.label}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- herramientas (bento) ---------- */
export const ToolsSection = () => (
  <section className="py-20 sm:py-24 bg-white">
    <div className="max-w-[1240px] mx-auto px-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
        <div>
          <p className="inline-flex px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
            Herramientas
          </p>
          <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#241E18] leading-tight max-w-xl">
            Todo lo que necesitas para proteger tus vídeos
          </h2>
        </div>
        <Link
          to="/editor"
          className="inline-flex items-center gap-2 text-[14.5px] font-extrabold text-[#F26F21] hover:gap-3 transition-all shrink-0"
        >
          Abrir el editor <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLS.map((t) => {
          const Icon = TOOL_ICONS[t.icon];
          return (
            <Link
              key={t.name}
              to="/editor"
              className="group rounded-3xl border border-[#F0E7DD] bg-[#FFFBF7] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#F26F21]/10 hover:border-[#F26F21]/40"
            >
              <div className="flex items-start justify-between mb-5">
                <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FFB81C] to-[#F26F21] flex items-center justify-center text-white shadow-md shadow-[#F26F21]/20 group-hover:scale-110 transition-transform">
                  <Icon size={22} />
                </span>
                {t.tag && (
                  <span className="px-2.5 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[10.5px] font-extrabold uppercase tracking-wide">{t.tag}</span>
                )}
              </div>
              <h3 className="font-display text-[18px] font-extrabold text-[#241E18]">{t.name}</h3>
              <p className="text-[13.5px] text-[#8A7F73] mt-2 leading-relaxed">{t.desc}</p>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- cómo funciona ---------- */
export const HowToSection = () => (
  <section className="py-20 sm:py-24 bg-[#FFF7F0]">
    <div className="max-w-[1240px] mx-auto px-6">
      <div className="text-center mb-14">
        <p className="inline-flex px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
          Así de fácil
        </p>
        <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#241E18]">Tres pasos y listo</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6 relative">
        {STEPS.map((s, i) => {
          const Icon = STEP_ICONS[s.icon];
          return (
            <div key={s.num} className="relative rounded-3xl bg-white border border-[#F0E7DD] p-8 text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#F26F21]/10">
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-[#F26F21] text-white text-[14px] font-extrabold flex items-center justify-center shadow-md shadow-[#F26F21]/30">
                {s.num}
              </span>
              <span className="inline-flex w-14 h-14 rounded-2xl bg-[#FFF3E9] text-[#F26F21] items-center justify-center mt-3 mb-5">
                <Icon size={26} />
              </span>
              <h3 className="font-display text-[19px] font-extrabold text-[#241E18]">{s.title}</h3>
              <p className="text-[13.5px] text-[#8A7F73] mt-2.5 leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------- casos de uso (scroll horizontal) ---------- */
export const UseCasesSection = () => (
  <section className="py-20 sm:py-24 bg-white overflow-hidden">
    <div className="max-w-[1240px] mx-auto px-6 mb-10">
      <p className="inline-flex px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
        Casos de uso
      </p>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#241E18] max-w-xl leading-tight">
          Quien graba, confía en Natur Blur
        </h2>
        <p className="text-[13.5px] text-[#8A7F73]">Desliza para ver más →</p>
      </div>
    </div>
    <div className="max-w-[1240px] mx-auto px-6">
      <div className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-hide">
        {USE_CASES.map((u) => (
          <article key={u.title} className="snap-start shrink-0 w-[280px] sm:w-[320px] rounded-3xl overflow-hidden border border-[#F0E7DD] bg-white group transition-all duration-300 hover:shadow-xl hover:shadow-[#F26F21]/10">
            <div className="h-44 overflow-hidden">
              <img
                src={IMAGES[u.img]}
                alt={u.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <h3 className="font-display text-[17px] font-extrabold text-[#241E18]">{u.title}</h3>
              <p className="text-[13px] text-[#8A7F73] mt-2 leading-relaxed">{u.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- testimonios (grid con destacado) ---------- */
export const TestimonialsSection = () => {
  const featured = TESTIMONIALS.find((t) => t.featured) || TESTIMONIALS[0];
  const rest = TESTIMONIALS.filter((t) => t !== featured);
  return (
    <section className="py-20 sm:py-24 bg-[#FFF7F0]">
      <div className="max-w-[1240px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="inline-flex px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
            Opiniones
          </p>
          <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#241E18]">Lo que dicen quienes ya lo usan</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-3xl bg-[#241E18] text-white p-9 flex flex-col justify-between">
            <div>
              <Quote size={28} className="text-[#FFB81C] mb-5" />
              <p className="font-display text-[21px] sm:text-[24px] font-bold leading-snug">"{featured.quote}"</p>
            </div>
            <div className="flex items-center gap-3.5 mt-8">
              <span className="w-12 h-12 rounded-full bg-[#F26F21] flex items-center justify-center text-[15px] font-extrabold">
                {featured.initials}
              </span>
              <div>
                <p className="text-[15px] font-extrabold">{featured.name}</p>
                <p className="text-[13px] text-white/55">{featured.role}</p>
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-1 gap-5">
            {rest.map((t) => (
              <div key={t.name} className="rounded-3xl bg-white border border-[#F0E7DD] p-6 flex items-start gap-4 transition-all duration-300 hover:shadow-lg hover:shadow-[#F26F21]/5">
                <span className="w-11 h-11 rounded-full bg-[#FFF3E9] text-[#F26F21] flex items-center justify-center text-[13px] font-extrabold shrink-0">
                  {t.initials}
                </span>
                <div>
                  <p className="text-[13.5px] text-[#57503F] leading-relaxed">"{t.quote}"</p>
                  <p className="text-[13px] font-extrabold text-[#241E18] mt-2.5">
                    {t.name} <span className="font-medium text-[#A69C90]">· {t.role}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- FAQ ---------- */
export const FAQSection = () => (
  <section className="py-20 sm:py-24 bg-white">
    <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-[1fr_1.5fr] gap-12">
      <div>
        <p className="inline-flex px-3 py-1 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-wider mb-4">
          FAQ
        </p>
        <h2 className="font-display text-[30px] sm:text-[38px] font-extrabold text-[#241E18] leading-tight">
          Preguntas frecuentes
        </h2>
        <p className="text-[14px] text-[#8A7F73] mt-4 max-w-sm">
          ¿No encuentras lo que buscas? Escríbenos y te respondemos en menos de 24 horas.
        </p>
        <Link
          to="/editor"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full border-2 border-[#241E18] text-[#241E18] text-[14px] font-extrabold hover:bg-[#241E18] hover:text-white transition-all"
        >
          Probar el editor <ArrowRight size={15} />
        </Link>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {FAQS.map((f, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="rounded-2xl border border-[#F0E7DD] bg-[#FFFBF7] px-6 data-[state=open]:border-[#F26F21]/50 transition-colors"
          >
            <AccordionTrigger className="text-left text-[15px] font-extrabold text-[#241E18] hover:no-underline py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-[13.5px] text-[#8A7F73] leading-relaxed pb-5">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* ---------- CTA final ---------- */
export const FinalCTA = () => (
  <section className="py-20 px-6">
    <div className="max-w-[1100px] mx-auto rounded-[36px] bg-gradient-to-br from-[#F26F21] to-[#FFB81C] px-8 py-16 text-center relative overflow-hidden">
      <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold text-white leading-tight relative">
        Tu próximo vídeo, protegido en minutos
      </h2>
      <p className="text-white/85 text-[15px] mt-4 max-w-lg mx-auto relative">
        Sube un vídeo, elige qué ocultar y descárgalo listo para publicar. Empieza gratis con 25 créditos.
      </p>
      <Link
        to="/editor"
        className="mt-9 inline-flex items-center gap-2 h-13 px-9 py-4 rounded-full bg-white text-[#F26F21] text-[15.5px] font-extrabold hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-0.5 transition-all relative"
      >
        Empezar ahora <ArrowRight size={17} />
      </Link>
    </div>
  </section>
);
